import { Icon } from "@iconify/react";
import * as Yup from "yup";
import { HStack, Error } from "../../shared/utils";
import { Input } from "../../shared/InputField";
import { useState } from "react";
import { ButtonSize, PrimaryCTAButton } from "../../shared/CTAButtonComponents";
import { logoDarkImage } from "../../shared/images";
import { UserType } from "../../types/auth";
import PasswordStrengthIndicator from "./passwordIndicator";
import { useFormik } from "formik";
import { useResetPassword } from "../../queries/auth";
import { decodeAuthToken } from "../../utils/auth";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../../utils/AmpAnalytics";
import { Dialog } from "@mui/material";
import TermsAndPolicy from "../../shared/TermsAndPolicy";
import { PrivacyPolicy } from "./privacyPolicy";
import { checkHeartBeat } from "../../api/auth";
export type ResetPasswordType = {
  password: string;
  confirmPassword: string;
  otp: string;
};

export const ResetPasswordPage = () => {
  const location = useLocation();
  let props: any = location.state;
  const email = sessionStorage.getItem("email");
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    header: string;
  }>({ open: false, header: "" });

  return (
    <div
      className="fixed top-0 right-0 left-0 z-40 bg-white shadow-box"
    >
      <div
        className={`flex h-14 shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-6 ${props.className}`}
      >
        {logoDarkImage ? (
          <img
            className="h-9 mx-auto w-auto"
            src={logoDarkImage}
            alt="hissa_logo"
          />
        ) : (
          <h2 className="text-4xl font-semibold text-secondary">{"hissa"}</h2>
        )}
        <form className="relative flex flex-1" action="#" method="GET"></form>
        <div className="hidden lg:block"></div>
      </div>

      <div className="w-screen px-4 px-md-0 h-screen bg-gradient-to-t from-gradientFrom to-gradientTo flex flex-col items-center justify-start overflow-y-auto">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-[450px] p-6 z-10 mt-8">
          <div className="flex justify-center mb-6">
            {logoDarkImage ? (
              <img
                className="h-12 w-28 mx-auto"
                src={logoDarkImage}
                alt="hissa_logo"
              />
            ) : (
              <h2 className="text-4xl font-semibold text-secondary">hissa</h2>
            )}
          </div>

          <ResetPassword
            email={email ? JSON.parse(email || "") : ""}
            selectedRole={props.selectedRole}
            transactionId={props.transactionId}
          />
        </div>

        <div className=" mt-2 mb-10">
          <PrivacyPolicy />
        </div>
      </div>
    </div>
  );
};

export const ResetPassword = (props: any) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { mutate: ResetPassword } = useResetPassword();

  const initialPasswordValues: ResetPasswordType = {
    password: "",
    confirmPassword: "",
    otp: "",
  };

  const passwordSchema = Yup.object().shape({
    otp: Yup.string().required("OTP is required"),
    password: Yup.string()
      .min(6, "Password length should be at least 6")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{6,})/,
        "Must Include at least 6 Characters, One Uppercase, One Lowercase, One Number and One Special Character"
      )
      .max(50, "Maximum 50 Characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .required("Password confirmation is required")
      .oneOf(
        [Yup.ref("password")],
        "Confirm password doesn't match the entered password."
      ),
  });

  const formik = useFormik<ResetPasswordType>({
    initialValues: initialPasswordValues,
    validationSchema: passwordSchema,
    onSubmit: (values: ResetPasswordType) => {
      setLoading(true);
      ResetPassword(
        {
          data: {
            email: props.email,
            type: props.selectedRole as UserType,
            password: values.password,
            confirmPassword: values.password,
            otp: values.otp,
            transactionId: props.transactionId,
          },
        },
        {
          onSuccess: async () => {
            setLoading(false);
            trackEvent("Generated New Password", {
              email: props.email,
              name: props.email,
            });
            toast("Password Updated Successfully", {
              type: "success",
              autoClose: 2000,
            });
            const heartbeat = await checkHeartBeat();
            if (!heartbeat) return;

            navigate("/landing/dashboard");

          },

          onError: (error: any) => {
            toast(error?.response?.data?.message || "Something went wrong!", {
              type: "error",
              autoClose: 2000,
            });
            setLoading(false);
          },
        }
      );
    },
  });

  return (
    <>
      <form className="" onSubmit={formik.handleSubmit}>
        <p className="text-center w-full md:w-[400px] mb-4">
          {"Please Enter new Password"}
        </p>

        <div className="bg-gray-100 rounded-md p-1 md:p-2 w-full md:w-[400px] mx-auto">
          <HStack className="justify-between">
            <div></div>
            <HStack>
              <img
                className="w-7 h-7 mr-2"
                src={"/media/user-circle.svg"}
                alt={"button-image"}
              />
              <p className="truncate">{props.email}</p>
            </HStack>
            <div className=""></div>
          </HStack>
        </div>

        <div className="mt-4">
          <>
            <div className="mx-auto w-full md:w-[400px]">
              <div className="relative">
                <Input
                  className="w-full pr-10"
                  type={"text"}
                  placeholder="OTP"
                  {...formik.getFieldProps("otp")}
                />
              </div>

              {formik.touched?.otp && formik.errors?.otp && (
                <Error
                  className="text-left md:w-[400px] mx-auto text-red-500 my-0"
                  text={formik.errors.otp}
                />
              )}
            </div>

            <div className="mx-auto w-full md:w-[400px] mt-2">
              <div className="relative">
                <Input
                  className="w-full pr-10"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  {...formik.getFieldProps("password")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {!showPassword ? (
                    <Icon
                      icon="garden:eye-hide-fill-12"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  ) : (
                    <Icon
                      icon="subway:eye"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  )}
                </button>
              </div>

              {formik.touched?.password && formik.errors?.password && (
                <Error
                  className="text-left md:w-[400px] mx-auto text-red-500 my-0"
                  text={formik.errors.password}
                />
              )}
            </div>
            <div className="mx-auto w-full md:w-[400px] mt-2">
              <div className="relative">
                <Input
                  className="w-full pr-10"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  {...formik.getFieldProps("confirmPassword")}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 mt-2 right-3 flex items-center text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {!showConfirmPassword ? (
                    <Icon
                      icon="garden:eye-hide-fill-12"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  ) : (
                    <Icon
                      icon="subway:eye"
                      className="text-gray-400 placeholderLoader rounded-lg"
                      height={18}
                    />
                  )}
                </button>
              </div>

              {formik.touched?.confirmPassword &&
                formik.errors?.confirmPassword && (
                  <Error
                    className="text-left md:w-[400px] mx-auto text-red-500 my-0"
                    text={formik.errors.confirmPassword}
                  />
                )}
            </div>
            <PasswordStrengthIndicator password={formik.values.password} />
          </>
        </div>
        <div className="mt-4 flex flex-col items-center">
          <div className="mt-6">
            <PrimaryCTAButton
              event-name="Sign in"
              className="w-32 text-center px-auto "
              buttonSize={ButtonSize.MEDIUM}
              loading={loading}
              type="submit"
            >
              <p className="w-full text-sm leading-0">{"Confirm"}</p>
            </PrimaryCTAButton>
          </div>
          <button
            type="button"
            onClick={() => {
              navigate("/login");
            }}
            className="underline text-xs pt-4 text-gray-500 hover:text-secondary font-semibold"
          >
            {"Back to Sign in"}
          </button>
        </div>
      </form>
    </>
  );
};
